import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { shallowEqual, useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { keepPreviousData, useInfiniteQuery } from '@zg-rentals/react-query5';

import { Box, Page } from '@zillow/constellation-10';
import { parseQueryString } from '@zg-rentals/string-utils';
import { logError } from '@zg-rentals/log-error';
import { CometUserRoleType, RentalManagerAccountViewZrmWebApiHandler_list } from '@zg-rentals/rental-manager-env';

import { RMWContext } from '../../providers/RMWContext';

import { AppSelectors, PropertiesSelectors } from '../../selectors';
import { useSelector } from '../../selectors/useSelector';

import PropertiesActions from '../../actions/PropertiesActions';

import accessUtils from '../../utils/accessUtils';
import contextUtils from '../../utils/contextUtils';
import {
  closeBanner,
  getPhotoUrls,
  removeQueryParam,
  renderInitialDisplayModal,
} from '../../utils/propertiesPageUtils';

import PropertiesConstants from '../../constants/PropertiesConstants';
import QueryConstants from '../../constants/QueryConstants';

import AddPropertyModalCSEvents from '../../model/ClickstreamEvent/AddPropertyModalCSEvents';
import ErrorRMW from '../../model/ErrorRMW';
import Properties from '../../model/Properties';
import PropertiesPageCSEvents from '../../model/ClickstreamEvent/PropertiesPageCSEvents';

import AddFeedListingsModal from '../../components/Modal/AddFeedListingsModal';
import AddPropertyModal from '../../components/Modal/AddPropertyModal';
import PaymentsAggregateViewModal from '../../components/Modal/PaymentsAggregateViewModal';
import AuthCheckWrapper from '../../containers/AuthCheckWrapper';
import HeadWrapper from '../../components/HeadWrapper';

import InfiniteScroller from '../../components/InfiniteScroller';
import SpinnerContainer from '../../components/SpinnerContainer';
import NotificationBanner from '../../components/NotificationBanner';
import PropertiesDisplay from '../../components/properties/PropertiesDisplay';

import { withErrorBoundary } from '../../components/ErrorBoundary';
import WizardUserOnboarding from '../../components/WizardUserOnboarding';

import EmailVerification from './components/EmailVerification';
import Notifications from './components/Notifcations';
import PageHeading from './components/PageHeading';
import SubHeadingSection from './components/SubHeadingSection';

import type {
  AccountViewListingDto,
  AccountViewListingsDto,
  RentalManagerKeyPagerDto,
} from '@zg-rentals/ts-rental-manager-api-client';
import type PropertiesPageConstants from './PropertiesPageConstants';
import type { PhotoUrlsProps } from '../../model/Property';

const defaultFilterData = {
  includeActive: PropertiesConstants.PROPERTIES_ACTIVE_DEFAULT,
  includeInactive: PropertiesConstants.PROPERTIES_INACTIVE_DEFAULT,
  searchText: PropertiesConstants.PROPERTIES_SEARCHTEXT_DEFAULT,
  startKeyExclusive: PropertiesConstants.PROPERTIES_START_KEY_EXCLUSIVE_DEFAULT,
  limit: PropertiesConstants.PROPERTIES_LIMIT_DEFAULT,
  includeArchived: PropertiesConstants.PROPERTIES_INCLUDE_ARCHIVED,
  includeAtRisk: PropertiesConstants.PROPERTIES_INCLUDE_AT_RISK,
  includeFreeBonusListingsOnly: PropertiesConstants.PROPERTIES_INCLUDE_FREE_BONUS_LISTINGS_ONLY,
  includeUnarchived: PropertiesConstants.PROPERTIES_INCLUDE_UNARCHIVED,
  includeFreeClaimedListingsOnly: PropertiesConstants.PROPERTIES_INCLUDE_FREE_CLAIMED_LISTINGS_ONLY,
  subNavFilterType: PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ALL,
};

const { PROPERTIES_SUBNAV_FILTER_TYPE } = PropertiesConstants;

const ALL_PROPERTIES_FILTER = {
  includeActive: true,
  includeFreeClaimedListingsOnly: false,
  includeInactive: true,
  includeAtRisk: false,
  includeFreeBonusListingsOnly: false,
  includeArchived: false,
  includeUnarchived: true,
  subNavFilterType: PROPERTIES_SUBNAV_FILTER_TYPE.ALL,
} as const;

const subNavFilterOptions = {
  options: PropertiesConstants.PROPERTIES_SUBNAV_FILTER_OPTIONS.reduce(
    (acc, subNav) => {
      acc[subNav.id] = { ...subNav };

      return acc;
    },
    {} as Record<string, PROPERTIES_SUBNAV_FILTER_OPTIONS_TYPE>,
  ),
};

type PROPERTIES_SUBNAV_FILTER_OPTIONS_TYPE = (typeof PropertiesConstants.PROPERTIES_SUBNAV_FILTER_OPTIONS)[0];

const PropertiesPage = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();

  const { paths } = useContext(RMWContext);

  const { cookieEnabledServices, isRunningInProd, isScreenMobile, properties, propertiesFilters, user } = useSelector(
    (state) => ({
      cookieEnabledServices: AppSelectors.makeCookieEnabledServices(state),
      isRunningInProd: AppSelectors.makeIsRunningInProd(state),
      isScreenMobile: AppSelectors.makeIsScreenMobile(state),
      properties: PropertiesSelectors.makeProperties(state),
      propertiesFilters: PropertiesSelectors.makePropertiesFilters(state),
      user: state.user,
    }),
    shallowEqual,
  );

  const [listingsLoading, setListingsLoading] = useState(true);
  const [totalListingsWithoutFilters, setTotalListingsWithoutFilters] = useState<null | number>(null);
  const [disableLoadMore, setDisableLoadMore] = useState(false);
  const [didPropertiesLoad, setDidPropertiesLoad] = useState(false);
  const [showAddFeedListingsModal, setShowAddFeedListingsModal] = useState(false);
  const [displayAddPropertyModal, setDisplayAddPropertyModal] = useState(false);
  const [displayPaymentsNotification, setDisplayPaymentsNotification] = useState(false);
  const [displayPaymentsAggregateViewModal, setDisplayPaymentsAggregateViewModal] = useState(false);
  const [displayApplicationsNotification, setDisplayApplicationsNotification] = useState(false);
  const [displayLeasesNotification, setDisplayLeasesNotification] = useState(false);
  const [displayListingClaimCompleteBanner, setDisplayListingClaimCompleteBanner] = useState(false);
  const [arePropertiesFiltersInitialized, setArePropertiesFiltersInitialized] = useState(false);

  const isCheckInitialDisplayModal = useRef<boolean>();
  const isFirstFetch = useRef<boolean>(true);
  const refetchRef = useRef<Record<string, boolean>>({});

  const canAccessOnboardingWizard = accessUtils.canAccessOnboardingWizard({
    cookieEnabledServices,
    user,
  });

  const [isViewingWizardUserOnboarding, setIsViewingWizardUserOnboarding] = useState(canAccessOnboardingWizard);

  const {
    listingCount: {
      active: propListingCountActiveListings,
      total: propListingCountTotalListings,
      archived: numArchivedListings,
      freeClaimedListingCount: numClaimedListings,
    },
    listings,
    pager,
    hasSubNavFiltersBeenTouched,
  } = properties;
  const {
    includeActive,
    includeFreeClaimedListingsOnly,
    includeInactive,
    includeArchived,
    includeUnarchived,
    includeAtRisk,
    includeFreeBonusListingsOnly,
    searchText,
    subNavFilterType,
    feedClaimAndConvertUpdate,
  } = propertiesFilters;
  const {
    features: { canAddListing, canViewClaimFeedListing, canAccessMainPayments },
    hasSeenPaymentsAggregatedViewModal,
    paymentsAcceptedCount,
    restrictions: { isAllowed: isUserAllowed },
    summary: { activeListingCount: userSummaryActiveListingCount, totalListingCount: userSummaryTotalListingCount },
    userToken,
  } = user;

  const { search, pathname } = location;

  const totalProperties =
    propListingCountActiveListings + propListingCountTotalListings + numClaimedListings + numArchivedListings;

  const queryParams = parseQueryString(search);

  const includeUserManagementCompanyId = user.roles.includes(CometUserRoleType.PROPERTY_MANAGER_COMPANY_ADMIN);

  const [currentQueryKey, currentQueryKeyString] = useMemo(() => {
    const queryKeyArr = ['getProperties', userToken];
    Object.keys(propertiesFilters).forEach((key) => {
      queryKeyArr.push(key + ':' + propertiesFilters[key]);
    });
    return [queryKeyArr, queryKeyArr.join('_')];
  }, [
    includeActive,
    includeInactive,
    includeArchived,
    includeUnarchived,
    includeAtRisk,
    includeFreeBonusListingsOnly,
    includeFreeClaimedListingsOnly,
    includeUserManagementCompanyId,
    searchText,
    subNavFilterType,
    feedClaimAndConvertUpdate,
  ]);

  const fetchProperties = async ({ pageParam = '' }) => {
    if (!refetchRef.current.hasOwnProperty(currentQueryKeyString as keyof typeof refetchRef)) {
      refetchRef.current[currentQueryKeyString as keyof typeof refetchRef] = true;
    }

    return RentalManagerAccountViewZrmWebApiHandler_list({
      ...propertiesFilters,
      startKeyExclusive: pageParam,
    });
  };

  const {
    data: propertiesData,
    fetchNextPage,
    error: getPropertiesError,
    isError: isGetPropertiesError,
    isLoading: isGetPropertiesLoading,
    isFetching: isGetPropertiesFetching,
  } = useInfiniteQuery({
    queryKey: currentQueryKey,
    queryFn: fetchProperties,
    initialPageParam: '',
    getNextPageParam: (lastPage: { pager: RentalManagerKeyPagerDto }) => lastPage.pager.endKey,
    enabled: arePropertiesFiltersInitialized,
    gcTime: 60 * 60 * 1000,
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
    refetchIntervalInBackground: true,
    refetchInterval: (data) => {
      if (data === undefined || refetchRef.current[currentQueryKeyString as keyof typeof refetchRef]) {
        return false;
      } else {
        return 1;
      }
    },
  });

  useEffect(() => {
    PropertiesPageCSEvents.ViewPropertiesPage({ user });
  }, []);

  useEffect(() => {
    if (isGetPropertiesLoading === false && isGetPropertiesFetching === false) {
      handlePropertiesLoaded({ propertiesData });
    }
  }, [isGetPropertiesLoading, isGetPropertiesFetching, propertiesData]);

  useEffect(() => {
    if (didPropertiesLoad) {
      setListingsLoading(false);
    }
  }, [didPropertiesLoad, totalProperties]);

  useEffect(() => {
    setDisableLoadMore(false);
  }, [listings]);

  useEffect(() => {
    if (isGetPropertiesError) {
      setDisableLoadMore(true);
      setDidPropertiesLoad(false);
      // @ts-expect-error
      const errorRMWModel = ErrorRMW(getPropertiesError.error);

      logError({
        error: errorRMWModel,
        context: 'fetchProperties',
      });
    }
  }, [isGetPropertiesError, getPropertiesError]);

  useMemo(() => {
    const { subNavFilterType = '', itc } = queryParams;
    setDisplayListingClaimCompleteBanner(itc === 'listing_claim_complete');

    const numActiveListings = propListingCountActiveListings || userSummaryActiveListingCount;
    const numTotalListings = propListingCountTotalListings || userSummaryTotalListingCount;
    const subNavFilterKey = subNavFilterType.toUpperCase().split('-').join('_');
    const includeArchived = subNavFilterKey === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED;

    defaultFilterData.includeArchived = includeArchived;
    defaultFilterData.includeUnarchived = !includeArchived;
    defaultFilterData.searchText = queryParams.searchText || '';

    if (subNavFilterKey in PROPERTIES_SUBNAV_FILTER_TYPE) {
      const stayOnAll = subNavFilterType === PROPERTIES_SUBNAV_FILTER_TYPE.ALL && numTotalListings > 0;
      const stayOnMarket = subNavFilterType === PROPERTIES_SUBNAV_FILTER_TYPE.ON_MARKET && numActiveListings > 0;
      const stayOnOffMarket =
        subNavFilterType === PROPERTIES_SUBNAV_FILTER_TYPE.OFF_MARKET &&
        Number(numTotalListings - numActiveListings) > 0;
      const stayOnAtRisk = subNavFilterType === PROPERTIES_SUBNAV_FILTER_TYPE.AT_RISK && numActiveListings > 0;
      const stayOnArchived = subNavFilterType === PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED && numTotalListings > 0;
      const stayOnBonus =
        subNavFilterType === PROPERTIES_SUBNAV_FILTER_TYPE.FREE_BONUS_LISTINGS && numActiveListings > 0;
      const stayOnClaimed =
        subNavFilterType === PROPERTIES_SUBNAV_FILTER_TYPE.CLAIMED &&
        numActiveListings > 0 &&
        user.features.canClaimFreeListings;

      const defaultFilterDataFlagsHelper = (optionKey: string) => {
        const flagsToUpdate = [
          'includeActive',
          'includeInactive',
          'includeAtRisk',
          'includeFreeClaimedListingsOnly',
          'includeFreeBonusListingsOnly',
          'includeArchived',
          'includeUnarchived',
        ] as const;

        flagsToUpdate.forEach((flag) => {
          defaultFilterData[flag] = subNavFilterOptions.options[optionKey][flag];
        });
      };

      if (
        stayOnAll ||
        stayOnMarket ||
        stayOnOffMarket ||
        stayOnAtRisk ||
        stayOnArchived ||
        stayOnBonus ||
        stayOnClaimed
      ) {
        defaultFilterDataFlagsHelper(subNavFilterType);
      } else if (defaultFilterData.subNavFilterType !== subNavFilterType) {
        // not to re-route if defaultFilterType === subNavFilterType query
        // set the subNav tag to default value
        defaultFilterDataFlagsHelper(defaultFilterData.subNavFilterType);
      }

      defaultFilterData.subNavFilterType =
        stayOnAll || stayOnMarket || stayOnOffMarket || stayOnAtRisk || stayOnArchived || stayOnClaimed
          ? PROPERTIES_SUBNAV_FILTER_TYPE[subNavFilterKey as keyof typeof PROPERTIES_SUBNAV_FILTER_TYPE]
          : defaultFilterData.subNavFilterType;
    }
    dispatch(PropertiesActions.updateFilters(hasSubNavFiltersBeenTouched ? propertiesFilters : defaultFilterData));
    setArePropertiesFiltersInitialized(true);
  }, []);

  useEffect(() => {
    // if there are no archived properties and user is on archived tab, switch to all
    if (
      !isGetPropertiesLoading &&
      numArchivedListings === 0 &&
      subNavFilterType === PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED
    ) {
      updateFilters({ update: ALL_PROPERTIES_FILTER });
    }
  }, [numArchivedListings]);

  useEffect(() => {
    // if there are no unarchived properties and user is on any tab other than archived or all, switch to all
    if (
      !isGetPropertiesLoading &&
      propListingCountTotalListings == 0 &&
      subNavFilterType !== PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED &&
      subNavFilterType !== PROPERTIES_SUBNAV_FILTER_TYPE.ALL
    ) {
      updateFilters({ update: ALL_PROPERTIES_FILTER });
    }
  }, [propListingCountTotalListings]);

  useEffect(() => {
    if (__SERVER__) {
      return;
    }
    //  TODO - revisit this if we are returning user to previous position
    window.scroll(0, 0);

    const isFromMarketingPaymentsRoute = contextUtils.isFromMarketingPaymentsRoute(search);
    const isFromMarketingLeasesRoute = contextUtils.isFromMarketingLeasesRoute(search);
    const isFromMarketingApplicationsRoute = contextUtils.isFromMarketingApplicationsRoute(search);

    if (isUserAllowed) {
      if (isFromMarketingPaymentsRoute) {
        setDisplayPaymentsNotification(true);
      } else if (isFromMarketingLeasesRoute) {
        setDisplayLeasesNotification(true);
      } else if (isFromMarketingApplicationsRoute && user.applicationsEnabled) {
        setDisplayApplicationsNotification(true);
      }
    }

    if (feedClaimAndConvertUpdate?.hasCompletedFeedClaimAndConvert) {
      dispatch(PropertiesActions.getListingConversionStatus());
    }

    return () => {
      // TODO: unmount/cleanup function - removes the startKeyExclusive
      // revisit to return user to their previous location when returning to page
      const update = { startKeyExclusive: '' };

      dispatch(PropertiesActions.updateFilters(update));
    };
  }, []);

  useEffect(() => {
    if (isCheckInitialDisplayModal.current && !isGetPropertiesLoading) {
      isCheckInitialDisplayModal.current = false;

      checkInitialDisplayModal();
    }
  }, [totalListingsWithoutFilters, isGetPropertiesLoading]);

  /**
   * checks for queries via left navigation
   */
  useEffect(() => {
    checkInitialDisplayModal();
  }, [queryParams[QueryConstants.POSTING_PATH], queryParams[QueryConstants.ADD_A_FEED]]);

  useEffect(() => {
    // need to make sure that createQueryObjectAndRedirectTo runs last
    setTimeout(() => {
      createQueryObjectAndRedirectTo(searchText, subNavFilterType);
    }, 0);
  }, [searchText, subNavFilterType]);

  useEffect(() => {
    if (canAccessMainPayments && !hasSeenPaymentsAggregatedViewModal && paymentsAcceptedCount >= 2) {
      setDisplayPaymentsAggregateViewModal(true);
    }
  }, [canAccessMainPayments]);

  const checkInitialDisplayModal = () => {
    renderInitialDisplayModal({
      search,
      paths,
      totalListingsWithoutFilters: totalListingsWithoutFilters || 0,
      showAddPropertyModal: displayAddPropertyModal,
      setShowAddPropertyModal: handleAddPropertyClick,
      canAccessOnboardingWizard: canAccessOnboardingWizard && isViewingWizardUserOnboarding,
      properties,
      setShowAddFeedListingsModal: showAddFeedListingsModalViaParam,
    });
  };

  // @ts-expect-error
  const handlePropertiesLoaded = ({ propertiesData }) => {
    // iterate over propertiesData.pages and combine listings from each page into a single list in order.
    // then create a new object containing everything from the last page of propertiesData but replace the listings with this new list
    // TODO: consider not storing properties in redux and just use react-query response here
    if (propertiesData?.pages.length) {
      const combinedListings: Array<Partial<AccountViewListingDto & { photoUrls: PhotoUrlsProps }>> = [];
      propertiesData.pages.forEach((page: AccountViewListingsDto) => {
        combinedListings.push(...page.listings);
      });
      const lastPage = propertiesData.pages[propertiesData.pages.length - 1];
      const updatedPropertiesData = {
        ...lastPage,
        listings: combinedListings,
      };

      combinedListings.forEach((listing) => {
        if (listing.photoKey) {
          listing.photoUrls = getPhotoUrls(isRunningInProd, listing.photoKey);
        }
      });

      const propertiesModel = Properties(updatedPropertiesData);

      dispatch(PropertiesActions.updateProperties(propertiesModel));
      setDisableLoadMore(false);
      setDidPropertiesLoad(true);
      if (isFirstFetch.current === true && updatedPropertiesData?.listingCount?.total) {
        const {
          listingCount: { total: totalListingsWithoutFilters },
        } = updatedPropertiesData;
        isCheckInitialDisplayModal.current = isUserAllowed;
        isFirstFetch.current = false;
        setTotalListingsWithoutFilters(totalListingsWithoutFilters);
      }
    }
  };

  const onSearchFilterUpdate = (value: string) => {
    updateFilters({ update: { searchText: value, startKeyExclusive: '' } });
  };

  const onBannerClose = (bannerToClose: (typeof PropertiesPageConstants)[keyof typeof PropertiesPageConstants]) => {
    closeBanner(
      bannerToClose,
      setDisplayPaymentsNotification,
      setDisplayApplicationsNotification,
      setDisplayLeasesNotification,
      setDisplayListingClaimCompleteBanner,
    );
  };

  const toggleArchivedView = () => {
    // if showArchive is true, append filter params to the url
    const queryParams = !includeArchived
      ? createQueryString('', PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED)
      : createQueryString('', subNavFilterType);

    let update;

    // is in archivedView
    if (includeArchived) {
      // going back to regular listings view
      const isOnMarket = subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ON_MARKET;
      const isOnClaimed = subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.CLAIMED;
      const isOffMarket = subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.OFF_MARKET;
      const isOnAtRisk = subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.AT_RISK;
      const isOnBonus = subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.FREE_BONUS_LISTINGS;

      update = {
        includeArchived: false,
        includeUnarchived: true,
        includeActive: !isOffMarket && !isOnAtRisk,
        includeFreeClaimedListingsOnly: isOnClaimed,
        includeInactive: !isOnMarket && !isOnAtRisk,
        includeAtRisk: isOnAtRisk,
        includeFreeBonusListingsOnly: isOnBonus,
        startKeyExclusive: '',
        searchText: '',
      };
    } else {
      // going to archived listings view
      update = {
        includeArchived: true,
        includeUnarchived: false,
        includeActive: true,
        includeFreeClaimedListingsOnly: false,
        includeInactive: true,
        includeAtRisk: false,
        includeFreeBonusListingsOnly: false,
        startKeyExclusive: '',
        searchText: '',
      };
    }

    window.router.transitionTo(`${paths.propertiesPage}${queryParams}`);

    updateFilters({ update });
  };

  const showAddPropertyModal = (shouldSendAnalytics: boolean) => {
    const shouldRedirectToPayments = contextUtils.isRedirectToPayments(search);

    if (canAddListing && isUserAllowed) {
      setDisplayAddPropertyModal(true);
    }

    if (shouldSendAnalytics) {
      AddPropertyModalCSEvents.Open({
        user,
        triggerObject: shouldRedirectToPayments ? 'start_collecting_rent_button' : undefined,
      });
    }
  };

  const showAddFeedListingsModalViaParam = () => {
    if (isUserAllowed && canViewClaimFeedListing) {
      handleShowAddFeedListingsModal(true);
    }
  };

  const updateFilters = ({
    update,
    newDisableLoadMore = false,
  }: {
    update: Partial<{
      includeArchived: boolean;
      includeUnarchived: boolean;
      includeActive: boolean;
      includeFreeClaimedListingsOnly: boolean;
      includeInactive: boolean;
      includeAtRisk: boolean;
      includeFreeBonusListingsOnly: boolean;
      startKeyExclusive: string;
      searchText: string;
    }>;
    newDisableLoadMore?: boolean;
  }) => {
    const updateKeys = Object.keys(update);
    const updateIsDifferent = updateKeys.some(
      (key) => propertiesFilters[key as keyof typeof update] !== update[key as keyof typeof update],
    );

    if (updateIsDifferent) {
      setListingsLoading(true);
      setDidPropertiesLoad(false);
      setDisableLoadMore(newDisableLoadMore);
      // reset scroll position if viewing sticky header
      if (listings.length > 5) {
        window.scroll(0, 0);
      }
      dispatch(PropertiesActions.updateFilters(update));
    }
  };

  const handleAddPropertyClick = () => {
    showAddPropertyModal(true);
    AddPropertyModalCSEvents.AddPropertyClick({ user });
  };

  const handleShowAddFeedListingsModal = (newValue: boolean) => {
    setShowAddFeedListingsModal(newValue);
  };

  const handleAddFeedListingsClick = () => {
    handleShowAddFeedListingsModal(true);
    AddPropertyModalCSEvents.AddFeedClick({ user });
  };

  const handleLoadMoreClick = () => {
    setDisableLoadMore(true);
    fetchNextPage();
  };

  const createQueryObjectAndRedirectTo = (searchText = '', subNavFilterType = '') => {
    const queryString = createQueryString(searchText, subNavFilterType);

    window.router.redirectTo({ search: queryString, pathname });
  };

  const createQueryString = (searchText = '', subNavFilterType = '') => {
    const queryObject = parseQueryString(search);

    queryObject.searchText = searchText;
    queryObject.subNavFilterType = subNavFilterType;

    return queryObject ? `?${new URLSearchParams(queryObject).toString()}` : '';
  };

  const canAccessFeatureNotifications = accessUtils.canAccessFeatureNotifications({
    cookieEnabledServices,
    user,
  });

  const isGetPropertiesFetchingAndNotRefetching =
    isGetPropertiesFetching && !refetchRef.current[currentQueryKeyString as keyof typeof refetchRef];

  const isContentLoading = isGetPropertiesLoading || listingsLoading || isGetPropertiesFetchingAndNotRefetching;

  return (
    <AuthCheckWrapper>
      <Page.Root
        data-testid="PropertiesPage"
        data-eventid={PropertiesPageCSEvents.ViewPropertiesPage.data_event_id}
        data-eventdescription="View properties page"
        css={{ backgroundColor: 'bg.softest', width: isScreenMobile ? '100%' : 'auto' }}
      >
        <HeadWrapper title="Properties" />
        {canAccessFeatureNotifications && <NotificationBanner />}
        <PageHeading
          handleAddPropertyClick={handleAddPropertyClick}
          handleAddFeedListingsClick={handleAddFeedListingsClick}
        />
        <EmailVerification />
        <Notifications
          displayPaymentsNotification={displayPaymentsNotification}
          displayApplicationsNotification={displayApplicationsNotification}
          displayLeasesNotification={displayLeasesNotification}
          displayListingClaimCompleteBanner={displayListingClaimCompleteBanner}
          handleBannerCloseClick={onBannerClose}
        />
        <Page.Content>
          <Box css={{ backgroundColor: 'bg.neutral', borderRadius: '200', border: '1px solid {colors.border.subtle}' }}>
            {(propListingCountTotalListings > 0 || numArchivedListings > 0) && (
              <SubHeadingSection
                searchText={queryParams.searchText || searchText}
                listingsLoading={isContentLoading}
                numDisplayedListings={listings.length}
                onSearchFilterUpdate={onSearchFilterUpdate}
                createQueryObjectAndRedirectTo={createQueryObjectAndRedirectTo}
                updateFiltersAndListings={updateFilters}
              />
            )}
            {isContentLoading ? (
              <SpinnerContainer height="300px" />
            ) : (
              <div>
                {canAccessOnboardingWizard && isViewingWizardUserOnboarding && (
                  <WizardUserOnboarding onClose={() => setIsViewingWizardUserOnboarding(false)} />
                )}
                <Page.Content css={{ marginBlock: '0', paddingInline: { base: '200 !important', lg: '0 !important' } }}>
                  <PropertiesDisplay
                    listings={listings}
                    isLoading={isContentLoading}
                    didPropertiesLoad={didPropertiesLoad}
                    showArchived={includeArchived}
                    onRemoveSearchTextClick={onSearchFilterUpdate}
                    hasAnyArchivedListings={numArchivedListings > 0}
                    toggleArchivedView={toggleArchivedView}
                  />
                </Page.Content>
              </div>
            )}
          </Box>
          {!isContentLoading && (
            <InfiniteScroller
              pager={pager}
              text="Load more properties"
              onLoadMoreClick={handleLoadMoreClick}
              disabled={disableLoadMore}
            />
          )}
        </Page.Content>
        {showAddFeedListingsModal && (
          <AddFeedListingsModal
            onClose={() => {
              handleShowAddFeedListingsModal(false);
              removeQueryParam([QueryConstants.ADD_A_FEED], location, history);
            }}
          />
        )}
        {displayAddPropertyModal && (
          <AddPropertyModal
            isOpen={displayAddPropertyModal}
            onClose={() => {
              removeQueryParam([QueryConstants.POSTING_PATH, QueryConstants.REDIRECT_TO], location, history);
              setDisplayAddPropertyModal(false);
            }}
          />
        )}
        {displayPaymentsAggregateViewModal && (
          <PaymentsAggregateViewModal onClose={() => setDisplayPaymentsAggregateViewModal(false)} />
        )}
      </Page.Root>
    </AuthCheckWrapper>
  );
};

export default withErrorBoundary(PropertiesPage);
